import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  RefreshCcw,
  TrendingDown,
  TrendingUp,
  Minus,
  Eye,
  Calendar,
  Filter,
  ChevronDown,
  ChevronUp,
  Shield,
  AlertCircle,
  Plus,
  Upload,
  X,
  Trash2,
  BarChart3,
  Link,
  Calculator,
  History,
  AlertOctagon,
} from "lucide-react";
import complianceApi from "../../api/complianceApi";
import { confirmSubmit } from "../../utils/confirmSubmit";

const COMPLIANCE_CATEGORIES = [
  { value: "GST", label: "GST Compliance" },
  { value: "TDS", label: "TDS Compliance" },
  { value: "INCOME_TAX", label: "Income Tax Compliance" },
  { value: "LEGAL", label: "Legal Compliance" },
  { value: "INTERNAL_ACCOUNTING", label: "Internal Accounting" },
];

const DOCUMENT_STATUSES = [
  { value: "PENDING", label: "Pending", color: "bg-yellow-100 text-yellow-800" },
  { value: "VERIFIED", label: "Verified", color: "bg-blue-100 text-blue-800" },
  { value: "DISPUTED", label: "Disputed", color: "bg-red-100 text-red-800" },
  { value: "BOOKED", label: "Booked", color: "bg-purple-100 text-purple-800" },
  { value: "FILED", label: "Filed", color: "bg-green-100 text-green-800" },
];

const DOCUMENT_FREQUENCIES = [
  { value: "MONTHLY", label: "Monthly" },
  { value: "QUARTERLY", label: "Quarterly" },
  { value: "YEARLY", label: "Yearly" },
  { value: "AD_HOC", label: "Ad-hoc" },
];

const GST_DOCUMENT_TYPES = [
  { value: "GSTR1", label: "GSTR-1" },
  { value: "GSTR1_ACK", label: "GSTR-1 Acknowledgement" },
  { value: "GSTR3B", label: "GSTR-3B" },
  { value: "GSTR3B_ACK", label: "GSTR-3B Acknowledgement" },
  { value: "GSTR9", label: "GSTR-9 Annual Return" },
  { value: "GSTR9C", label: "GSTR-9C Reconciliation" },
  { value: "GST_CHALLAN", label: "GST Payment Challan" },
  { value: "GST_CREDIT_NOTE", label: "GST Credit Note" },
  { value: "GST_DEBIT_NOTE", label: "GST Debit Note" },
  { value: "ITC_04", label: "ITC-04" },
  { value: "RFD_01", label: "RFD-01 Refund Application" },
  { value: "OTHER", label: "Other GST Document" },
];

const TDS_DOCUMENT_TYPES = [
  { value: "FORM_24Q", label: "Form 24Q" },
  { value: "FORM_26Q", label: "Form 26Q" },
  { value: "FORM_27Q", label: "Form 27Q" },
  { value: "FORM_27EQ", label: "Form 27EQ" },
  { value: "TDS_CHALLAN", label: "TDS Payment Challan" },
  { value: "TDS_CERTIFICATE", label: "TDS Certificate" },
  { value: "OTHER", label: "Other TDS Document" },
];

const INCOME_TAX_DOCUMENT_TYPES = [
  { value: "ITR1", label: "ITR-1 Sahaj" },
  { value: "ITR2", label: "ITR-2" },
  { value: "ITR3", label: "ITR-3" },
  { value: "ITR4", label: "ITR-4 Sugam" },
  { value: "ITR5", label: "ITR-5" },
  { value: "ITR6", label: "ITR-6" },
  { value: "ITR7", label: "ITR-7" },
  { value: "ITR_ACK", label: "ITR Acknowledgement" },
  { value: "ADVANCE_TAX_CHALLAN", label: "Advance Tax Challan" },
  { value: "SELF_ASSESSMENT_TAX", label: "Self Assessment Tax Challan" },
  { value: "TAX_AUDIT_REPORT", label: "Tax Audit Report" },
  { value: "FORM_3CD", label: "Form 3CD" },
  { value: "FORM_3CA", label: "Form 3CA" },
  { value: "FORM_3CB", label: "Form 3CB" },
  { value: "OTHER", label: "Other Income Tax Document" },
];

const LEGAL_DOCUMENT_TYPES = [
  { value: "ROC_ANNUAL_FILING", label: "ROC Annual Filing" },
  { value: "AOA_MOA", label: "AOA/MOA" },
  { value: "BOARD_RESOLUTION", label: "Board Resolution" },
  { value: "SHARE_CERTIFICATE", label: "Share Certificate" },
  { value: "STATUTORY_REGISTER", label: "Statutory Register" },
  { value: "COMPLIANCE_CERTIFICATE", label: "Compliance Certificate" },
  { value: "DIR_3_KYC", label: "DIR-3 KYC" },
  { value: "MGT_7", label: "MGT-7 Annual Return" },
  { value: "AOC_4", label: "AOC-4 Financial Statements" },
  { value: "ADT_1", label: "ADT-1 Auditor Appointment" },
  { value: "OTHER", label: "Other Legal Document" },
];

const INTERNAL_ACCOUNTING_DOCUMENT_TYPES = [
  { value: "DEPRECIATION_SCHEDULE", label: "Depreciation Schedule" },
  { value: "PREPAID_REVERSAL", label: "Prepaid Expense Reversal" },
  { value: "PROVISION_SCHEDULE", label: "Provision Schedule" },
  { value: "BANK_RECONCILIATION", label: "Bank Reconciliation Statement" },
  { value: "STOCK_STATEMENT", label: "Stock/Inventory Statement" },
  { value: "DEBTORS_CREDITORS_AGING", label: "Debtors/Creditors Aging" },
  { value: "FIXED_ASSET_REGISTER", label: "Fixed Asset Register" },
  { value: "TRIAL_BALANCE", label: "Trial Balance" },
  { value: "LEDGER_EXTRACT", label: "Ledger Extract" },
  { value: "JOURNAL_VOUCHER", label: "Journal Voucher" },
  { value: "PURCHASE_INVOICE", label: "Purchase Invoice" },
  { value: "SALES_INVOICE", label: "Sales Invoice" },
  { value: "CREDIT_NOTE", label: "Credit Note" },
  { value: "DEBIT_NOTE", label: "Debit Note" },
  { value: "EXPENSE_VOUCHER", label: "Expense Voucher" },
  { value: "OTHER", label: "Other Accounting Document" },
];

const getDocumentTypesByCategory = (category) => {
  switch (category) {
    case "GST":
      return GST_DOCUMENT_TYPES;
    case "TDS":
      return TDS_DOCUMENT_TYPES;
    case "INCOME_TAX":
      return INCOME_TAX_DOCUMENT_TYPES;
    case "LEGAL":
      return LEGAL_DOCUMENT_TYPES;
    case "INTERNAL_ACCOUNTING":
      return INTERNAL_ACCOUNTING_DOCUMENT_TYPES;
    default:
      return [];
  }
};

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const QUARTERS = [
  { value: 1, label: "Q1 (Apr-Jun)" },
  { value: 2, label: "Q2 (Jul-Sep)" },
  { value: 3, label: "Q3 (Oct-Dec)" },
  { value: 4, label: "Q4 (Jan-Mar)" },
];

const getCurrentFiscalYear = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  if (month >= 3) {
    return `${year}-${year + 1}`;
  }
  return `${year - 1}-${year}`;
};

const getFiscalYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const options = [];
  for (let i = 2; i >= -2; i--) {
    const startYear = currentYear - i;
    options.push(`${startYear}-${startYear + 1}`);
  }
  return options;
};

const ComplianceTab = ({ selectedClientData, onRefreshAccounts }) => {
  const token = useSelector((state) => state.auth.access_token);
  const accountId = selectedClientData?.id;

  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [riskScore, setRiskScore] = useState(null);
  const [riskHistory, setRiskHistory] = useState([]);
  const [tracker, setTracker] = useState(null);
  const [trackerType, setTrackerType] = useState("monthly");
  const [activeSection, setActiveSection] = useState("dashboard");
  
  // Dashboard data
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [upcomingDocs, setUpcomingDocs] = useState([]);
  const [overdueDocs, setOverdueDocs] = useState([]);
  const [recentlyCompleted, setRecentlyCompleted] = useState([]);
  const [calculatingRisk, setCalculatingRisk] = useState(false);
  
  // Linking modal
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkingDocId, setLinkingDocId] = useState(null);
  const [linkedDocs, setLinkedDocs] = useState([]);

  const [filters, setFilters] = useState({
    category: "all",
    status: "all",
    fiscalYear: "all",
  });

  const [search, setSearch] = useState("");
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [auditTrail, setAuditTrail] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);

  // Upload Modal State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    complianceCategory: "",
    documentType: "",
    documentTypeOther: "",
    frequency: "MONTHLY",
    fiscalYear: getCurrentFiscalYear(),
    periodMonth: null,
    periodQuarter: null,
    gstin: "",
    dueDate: "",
    remarks: "",
    isYearEndCritical: false,
  });

  const fetchDocuments = async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const res = await complianceApi.getDocumentsByAccount(accountId, token);
      setDocuments(res || []);
    } catch (error) {
      console.error("Error fetching compliance documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRiskScore = async () => {
    if (!accountId) return;
    try {
      const res = await complianceApi.getRiskScore(accountId, token);
      setRiskScore(res);
    } catch (error) {
      console.error("Error fetching risk score:", error);
    }
  };

  const fetchRiskHistory = async () => {
    if (!accountId) return;
    try {
      const res = await complianceApi.getRiskHistory(accountId, token);
      setRiskHistory(res || []);
    } catch (error) {
      console.error("Error fetching risk history:", error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [summary, upcoming, overdue, recent] = await Promise.all([
        complianceApi.getDashboardSummary(null, token),
        complianceApi.getUpcomingDocuments(7, token),
        complianceApi.getOverdueDocuments(token),
        complianceApi.getRecentlyCompleted(30, token),
      ]);
      setDashboardSummary(summary);
      setUpcomingDocs(upcoming || []);
      setOverdueDocs(overdue || []);
      setRecentlyCompleted(recent || []);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  const fetchTracker = async (category = "GST", type = "monthly") => {
    if (!accountId) return;
    const fiscalYear = filters.fiscalYear === "all" ? getCurrentFiscalYear() : filters.fiscalYear;
    try {
      let res;
      if (type === "monthly") {
        res = await complianceApi.getMonthlyTracker(accountId, category, fiscalYear, token);
      } else {
        res = await complianceApi.getQuarterlyTracker(accountId, category, fiscalYear, token);
      }
      setTracker(res);
    } catch (error) {
      console.error("Error fetching tracker:", error);
    }
  };

  const handleCalculateRisk = async () => {
    if (!accountId) return;
    setCalculatingRisk(true);
    try {
      const fiscalYear = filters.fiscalYear === "all" ? getCurrentFiscalYear() : filters.fiscalYear;
      const res = await complianceApi.calculateRiskScore(accountId, fiscalYear, token);
      setRiskScore(res);
      toast.success("Risk score calculated successfully");
      fetchRiskHistory();
    } catch (error) {
      console.error("Error calculating risk score:", error);
      toast.error("Failed to calculate risk score");
    } finally {
      setCalculatingRisk(false);
    }
  };

  const fetchLinkedDocuments = async (docId) => {
    try {
      const res = await complianceApi.getLinkedDocuments(docId, token);
      setLinkedDocs(res || []);
    } catch (error) {
      console.error("Error fetching linked documents:", error);
    }
  };

  const handleLinkDocument = async (parentDocId, childDocId, linkageType) => {
    try {
      await complianceApi.linkDocument(childDocId, { parentDocumentId: parentDocId, linkageType }, token);
      toast.success("Documents linked successfully");
      setShowLinkModal(false);
      fetchDocuments();
    } catch (error) {
      console.error("Error linking documents:", error);
      toast.error("Failed to link documents");
    }
  };

  const fetchAuditTrail = async (docId) => {
    setLoadingAudit(true);
    try {
      const res = await complianceApi.getDocumentAuditTrail(docId, token);
      setAuditTrail(res || []);
    } catch (error) {
      console.error("Error fetching audit trail:", error);
    } finally {
      setLoadingAudit(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchRiskScore();
    fetchRiskHistory();
    fetchDashboardData();
  }, [accountId, token]);

  useEffect(() => {
    if (activeSection === "tracker" && filters.category !== "all") {
      fetchTracker(filters.category, trackerType);
    }
  }, [activeSection, filters.category, filters.fiscalYear, trackerType]);

  const handleExpandDoc = async (docId) => {
    if (expandedDoc === docId) {
      setExpandedDoc(null);
      setAuditTrail([]);
    } else {
      setExpandedDoc(docId);
      await fetchAuditTrail(docId);
    }
  };

  const filteredDocuments = useMemo(() => {
    let filtered = [...documents];

    if (filters.category !== "all") {
      filtered = filtered.filter(
        (doc) => doc.complianceCategory === filters.category
      );
    }

    if (filters.status !== "all") {
      filtered = filtered.filter((doc) => doc.status === filters.status);
    }

    if (filters.fiscalYear && filters.fiscalYear !== "all") {
      filtered = filtered.filter(
        (doc) => doc.fiscalYear === filters.fiscalYear
      );
    }

    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (doc) =>
          doc.documentType?.toLowerCase().includes(searchLower) ||
          doc.fileName?.toLowerCase().includes(searchLower) ||
          doc.gstin?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [documents, filters, search]);

  const getStatusBadge = (status) => {
    const statusConfig = DOCUMENT_STATUSES.find((s) => s.value === status);
    return statusConfig ? statusConfig.color : "bg-gray-100 text-gray-800";
  };

  const getRiskCategoryColor = (category) => {
    switch (category) {
      case "LOW":
        return "text-green-600 bg-green-50";
      case "MEDIUM":
        return "text-yellow-600 bg-yellow-50";
      case "HIGH":
        return "text-orange-600 bg-orange-50";
      case "CRITICAL":
        return "text-red-600 bg-red-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case "UP":
        return <TrendingUp className="h-4 w-4 text-red-500" />;
      case "DOWN":
        return <TrendingDown className="h-4 w-4 text-green-500" />;
      default:
        return <Minus className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleRefresh = () => {
    fetchDocuments();
    fetchRiskScore();
    fetchRiskHistory();
    fetchDashboardData();
    if (activeSection === "tracker" && filters.category !== "all") {
      fetchTracker(filters.category, trackerType);
    }
  };

  // Upload Modal Functions
  const resetUploadForm = () => {
    setUploadForm({
      complianceCategory: "",
      documentType: "",
      documentTypeOther: "",
      frequency: "MONTHLY",
      fiscalYear: getCurrentFiscalYear(),
      periodMonth: null,
      periodQuarter: null,
      gstin: "",
      dueDate: "",
      remarks: "",
      isYearEndCritical: false,
    });
    setSelectedFile(null);
  };

  const handleOpenUploadModal = () => {
    resetUploadForm();
    setShowUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    resetUploadForm();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadFormChange = (field, value) => {
    setUploadForm((prev) => {
      const updated = { ...prev, [field]: value };
      // Reset document type when category changes
      if (field === "complianceCategory") {
        updated.documentType = "";
        updated.documentTypeOther = "";
      }
      // Reset period fields based on frequency
      if (field === "frequency") {
        if (value === "MONTHLY") {
          updated.periodQuarter = null;
        } else if (value === "QUARTERLY") {
          updated.periodMonth = null;
        } else {
          updated.periodMonth = null;
          updated.periodQuarter = null;
        }
      }
      return updated;
    });
  };

  const handleUploadDocument = async () => {
    // Validation
    if (!uploadForm.complianceCategory) {
      toast.error("Please select a compliance category");
      return;
    }
    if (!uploadForm.documentType) {
      toast.error("Please select a document type");
      return;
    }
    if (uploadForm.documentType === "OTHER" && !uploadForm.documentTypeOther) {
      toast.error("Please specify the document type");
      return;
    }
    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return;
    }

    setUploading(true);
    try {
      const requestData = {
        accountId: accountId,
        complianceCategory: uploadForm.complianceCategory,
        documentType: uploadForm.documentType,
        documentTypeOther: uploadForm.documentType === "OTHER" ? uploadForm.documentTypeOther : null,
        frequency: uploadForm.frequency,
        fiscalYear: uploadForm.fiscalYear,
        periodMonth: uploadForm.frequency === "MONTHLY" ? uploadForm.periodMonth : null,
        periodQuarter: uploadForm.frequency === "QUARTERLY" ? uploadForm.periodQuarter : null,
        gstin: uploadForm.gstin || null,
        dueDate: uploadForm.dueDate ? new Date(uploadForm.dueDate).toISOString() : null,
        remarks: uploadForm.remarks || null,
        isYearEndCritical: uploadForm.isYearEndCritical,
      };

      await complianceApi.uploadDocument(requestData, selectedFile, token);
      toast.success("Compliance document uploaded successfully!");
      handleCloseUploadModal();
      fetchDocuments();
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error(error.message || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    const confirmed = await confirmSubmit({
      title: "Delete Document",
      text: "Are you sure you want to delete this compliance document?",
      confirmText: "Delete",
    });
    if (!confirmed) return;

    try {
      await complianceApi.deleteDocument(docId, token);
      toast.success("Document deleted successfully");
      fetchDocuments();
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Failed to delete document");
    }
  };

  const handleUpdateStatus = async (docId, newStatus) => {
    try {
      await complianceApi.updateDocumentStatus(docId, { status: newStatus }, token);
      toast.success("Status updated successfully");
      fetchDocuments();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const documentStats = useMemo(() => {
    const stats = {
      total: documents.length,
      pending: documents.filter((d) => d.status === "PENDING").length,
      verified: documents.filter((d) => d.status === "VERIFIED").length,
      filed: documents.filter((d) => d.status === "FILED").length,
      disputed: documents.filter((d) => d.status === "DISPUTED").length,
    };
    return stats;
  }, [documents]);

  return (
    <div className="space-y-4 bg-white p-3 rounded-lg">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-xl font-semibold text-gray-900">
          Compliance Management
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleOpenUploadModal}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary transition-colors text-sm"
          >
            <Plus className="h-4 w-4" />
            <span>Add Document</span>
          </button>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2">
        <div className="bg-blue-50 rounded-lg px-2 py-1 border border-blue-100 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            <span className="text-sm text-blue-700">Total</span>
          </div>
          <div className="text-2xl font-bold text-blue-800">
            {documentStats.total}
          </div>
        </div>
        <div className="bg-yellow-50 rounded-lg px-2 py-1 border border-yellow-100 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-yellow-600" />
            <span className="text-sm text-yellow-700">Pending</span>
          </div>
          <div className="text-2xl font-bold text-yellow-800">
            {documentStats.pending}
          </div>
        </div>
        <div className="bg-green-50 rounded-lg px-2 py-1 border border-green-100 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-700">Filed</span>
          </div>
          <div className="text-2xl font-bold text-green-800">
            {documentStats.filed}
          </div>
        </div>
        <div className="bg-purple-50 rounded-lg px-2 py-1 border border-purple-100 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-purple-600" />
            <span className="text-sm text-purple-700">Verified</span>
          </div>
          <div className="text-2xl font-bold text-purple-800">
            {documentStats.verified}
          </div>
        </div>
        <div className="bg-red-50 rounded-lg px-2 py-1 border border-red-100 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <span className="text-sm text-red-700">Disputed</span>
          </div>
          <div className="text-2xl font-bold text-red-800">
            {documentStats.disputed}
          </div>
        </div>
      </div>

      {/* Risk Score Card */}
      {riskScore && (
        <div className="bg-white border border-gray-200 rounded-lg p-2">
          <div className="flex items-center justify-between mb-1">
            <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="h-5 w-5 text-indigo-600" />
              Risk Assessment
            </div>
            <span
              className={`px-3 py-1 rounded-full text-xs font-medium ${getRiskCategoryColor(
                riskScore.riskCategory
              )}`}
            >
              {riskScore.riskCategory} Risk
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div>
              <div className="text-sm text-gray-500">Overall Score</div>
              <div className="flex items-center gap-2">
                <div className="text-2xl font-bold text-gray-900">
                  {riskScore.riskScore}
                </div>
                {getTrendIcon(riskScore.trend)}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Late Filing</div>
              <div className="text-lg font-semibold text-gray-700">
                {riskScore.lateFilingScore || 0}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Overdue</div>
              <div className="text-lg font-semibold text-gray-700">
                {riskScore.overdueScore || 0}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Disputed</div>
              <div className="text-lg font-semibold text-gray-700">
                {riskScore.disputedScore || 0}
              </div>
            </div>
          </div>
          {riskScore.mitigationActions && (
            <div className="mt-1 p-2 bg-amber-50 rounded border border-amber-200">
              <p className="text-sm text-amber-800">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                {riskScore.mitigationActions}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Section Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1 overflow-x-auto">
          {[
            { id: "dashboard", label: "Dashboard", icon: BarChart3 },
            { id: "documents", label: "Documents", icon: FileText },
            { id: "risk", label: "Risk Analysis", icon: Shield },
            { id: "tracker", label: "Tracker", icon: Calendar },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`flex items-center gap-2 px-1 py-0.5 border-b-2 text-sm font-medium transition-colors ${
                activeSection === tab.id
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <select
          className="border rounded-lg px-3 py-2 text-sm"
          value={filters.category}
          onChange={(e) => setFilters({ ...filters, category: e.target.value })}
        >
          <option value="all">All Categories</option>
          {COMPLIANCE_CATEGORIES.map((cat) => (
            <option key={cat.value} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select>

        <select
          className="border rounded-lg px-3 py-2 text-sm"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="all">All Statuses</option>
          {DOCUMENT_STATUSES.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>

        <select
          className="border rounded-lg px-3 py-2 text-sm"
          value={filters.fiscalYear}
          onChange={(e) =>
            setFilters({ ...filters, fiscalYear: e.target.value })
          }
        >
          <option value="all">All Fiscal Years</option>
          {getFiscalYearOptions().map((fy) => (
            <option key={fy} value={fy}>
              FY {fy}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Search documents..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm"
        />
      </div>

      {/* Dashboard Section */}
      {activeSection === "dashboard" && (
        <div className="space-y-4">
          {/* Summary Cards */}
          {dashboardSummary && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              <div className="bg-blue-50 rounded-lg px-2 py-1 border border-blue-100 flex flex-row items-center justify-between">
                <div className="text-sm text-blue-700">Total Documents</div>
                <div className="text-xl font-semibold text-blue-800">{dashboardSummary.totalDocuments || 0}</div>
              </div>
              <div className="bg-yellow-50 rounded-lg px-2 py-1 border border-yellow-100 flex flex-row items-center justify-between">
                <div className="text-sm text-yellow-700">Upcoming</div>
                <div className="text-xl font-semibold text-yellow-800">{dashboardSummary.upcomingCount || 0}</div>
              </div>
              <div className="bg-red-50 rounded-lg px-2 py-1 border border-red-100 flex flex-row items-center justify-between">
                <div className="text-sm text-red-700">Overdue</div>
                <div className="text-xl font-semibold text-red-800">{dashboardSummary.overdueCount || 0}</div>
              </div>
              <div className="bg-green-50 rounded-lg px-2 py-1 border border-green-100 flex flex-row items-center justify-between">
                <div className="text-sm text-green-700">Completed</div>
                <div className="text-xl font-semibold text-green-800">{dashboardSummary.completedCount || 0}</div>
              </div>
              <div className="bg-purple-50 rounded-lg px-2 py-1 border border-purple-100 flex flex-row items-center justify-between">
                <div className="text-sm text-purple-700">Pending Verification</div>
                <div className="text-xl font-semibold text-purple-800">{dashboardSummary.pendingVerificationCount || 0}</div>
              </div>
              <div className="bg-indigo-50 rounded-lg px-2 py-1 border border-indigo-100 flex flex-row items-center justify-between">
                <div className="text-sm text-indigo-700">Completion Rate</div>
                <div className="text-xl font-semibold text-indigo-800">{dashboardSummary.completionRate || 0}%</div>
              </div>
            </div>
          )}

          {/* Overdue Documents */}
          <div className="bg-white border border-red-200 rounded-lg p-2">
            <div className="text-lg font-semibold text-red-700 flex items-center gap-2 mb-1">
              <AlertOctagon className="h-5 w-5" />
              Overdue Documents ({overdueDocs.length})
            </div>
            {overdueDocs.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {overdueDocs.slice(0, 10).map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-2 bg-red-50 rounded border border-red-100">
                    <div>
                      <div className="font-medium text-gray-900">{doc.documentType}</div>
                      <div className="text-xs text-gray-500">{doc.accountName} • Due: {formatDate(doc.dueDate)}</div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(doc.status)}`}>
                      {doc.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 text-sm">No overdue documents</div>
            )}
          </div>

          {/* Upcoming Documents */}
          <div className="bg-white border border-yellow-200 rounded-lg p-2">
            <div className="text-lg font-semibold text-yellow-700 flex items-center gap-2 mb-1">
              <Clock className="h-5 w-5" />
              Upcoming Documents - Next 7 Days ({upcomingDocs.length})
            </div>
            {upcomingDocs.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {upcomingDocs.slice(0, 10).map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-2 bg-yellow-50 rounded border border-yellow-100">
                    <div>
                      <div className="font-medium text-gray-900">{doc.documentType}</div>
                      <div className="text-xs text-gray-500">{doc.accountName} • Due: {formatDate(doc.dueDate)}</div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(doc.status)}`}>
                      {doc.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 text-sm">No upcoming documents in next 7 days</div>
            )}
          </div>

          {/* Recently Completed */}
          <div className="bg-white border border-green-200 rounded-lg p-2">
            <div className="text-lg font-semibold text-green-700 flex items-center gap-2 mb-1">
              <CheckCircle className="h-5 w-5" />
              Recently Completed - Last 30 Days ({recentlyCompleted.length})
            </div>
            {recentlyCompleted.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {recentlyCompleted.slice(0, 10).map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-100">
                    <div>
                      <div className="font-medium text-gray-900">{doc.documentType}</div>
                      <div className="text-xs text-gray-500">{doc.accountName} • Filed: {formatDate(doc.filedDate)}</div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(doc.status)}`}>
                      {doc.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 text-sm">No recently completed documents</div>
            )}
          </div>
        </div>
      )}

      {/* Risk Analysis Section */}
      {activeSection === "risk" && (
        <div className="space-y-4">
          {/* Risk Score Card with Calculate Button */}
          <div className="bg-white border border-gray-200 rounded-lg p-2">
            <div className="flex items-center justify-between mb-2">
              <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Shield className="h-5 w-5 text-indigo-600" />
                Current Risk Assessment
              </div>
              <button
                onClick={handleCalculateRisk}
                disabled={calculatingRisk}
                className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm disabled:opacity-50"
              >
                {calculatingRisk ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Calculating...
                  </>
                ) : (
                  <>
                    <Calculator className="h-4 w-4" />
                    Calculate Risk
                  </>
                )}
              </button>
            </div>

            {riskScore ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                <div className="text-center p-2 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Overall Score</div>
                  <div className="text-xl font-bold text-gray-900">{riskScore.riskScore}</div>
                  <div className="flex items-center justify-center gap-1 mt-1">
                    {getTrendIcon(riskScore.trend)}
                    <span className="text-xs text-gray-500">{riskScore.trend || "STABLE"}</span>
                  </div>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Category</div>
                  <span className={`inline-block px-3 py-1 mt-1 rounded-full text-xs font-medium ${getRiskCategoryColor(riskScore.riskCategory)}`}>
                    {riskScore.riskCategory}
                  </span>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Late Filing</div>
                  <div className="text-xl font-bold text-orange-600">{riskScore.lateFilingScore || 0}</div>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Overdue</div>
                  <div className="text-xl font-bold text-red-600">{riskScore.overdueScore || 0}</div>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Disputed</div>
                  <div className="text-xl font-bold text-yellow-600">{riskScore.disputedScore || 0}</div>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500">Complexity</div>
                  <div className="text-xl font-bold text-purple-600">{riskScore.complexityScore || 0}</div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Shield className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <div>No risk score available. Click "Calculate Risk" to generate.</div>
              </div>
            )}

            {riskScore?.mitigationActions && (
              <div className="mt-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <AlertCircle className="h-4 w-4 inline mr-1" />
                  <strong>Recommended Actions:</strong> {riskScore.mitigationActions}
                </p>
              </div>
            )}
          </div>

          {/* Risk History */}
          <div className="bg-white border border-gray-200 rounded-lg p-2">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-2">
              <History className="h-5 w-5 text-gray-600" />
              Risk Score History
            </h3>
            {riskHistory.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs text-slate-700">Fiscal Year</th>
                      <th className="px-3 py-2 text-left text-xs text-slate-700">Score</th>
                      <th className="px-3 py-2 text-left text-xs text-slate-700">Category</th>
                      <th className="px-3 py-2 text-left text-xs text-slate-700">Trend</th>
                      <th className="px-3 py-2 text-left text-xs text-slate-700">Calculated At</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {riskHistory.map((score, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-xs">{score.fiscalYear}</td>
                        <td className="px-3 py-2 text-xs font-bold">{score.riskScore}</td>
                        <td className="px-3 py-2 text-xs">
                          <span className={`px-2 text-[10px] py-1 rounded-full font-medium ${getRiskCategoryColor(score.riskCategory)}`}>
                            {score.riskCategory}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <div className="flex items-center gap-1">
                            {getTrendIcon(score.trend)}
                            <span className="text-xs">{score.trend || "STABLE"}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">{formatDate(score.calculatedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-4">No risk history available</p>
            )}
          </div>
        </div>
      )}

      {/* Documents Section */}
      {activeSection === "documents" && (
        <div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredDocuments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-slate-700">#</th>
                    <th className="px-3 py-2 text-left text-slate-700">
                      Document Type
                    </th>
                    <th className="px-3 py-2 text-left text-slate-700">
                      Category
                    </th>
                    <th className="px-3 py-2 text-left text-slate-700">
                      GSTIN
                    </th>
                    <th className="px-3 py-2 text-left text-slate-700">
                      Period
                    </th>
                    <th className="px-3 py-2 text-left text-slate-700">
                      Due Date
                    </th>
                    <th className="px-3 py-2 text-left text-slate-700">
                      Status
                    </th>
                    <th className="px-3 py-2 text-center text-slate-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredDocuments.map((doc, index) => (
                    <React.Fragment key={doc.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-3 py-2">{index + 1}</td>
                        <td className="px-3 py-2 font-medium">
                          {doc.documentType}
                          {doc.documentTypeOther && (
                            <span className="text-gray-500 text-xs block">
                              ({doc.documentTypeOther})
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {COMPLIANCE_CATEGORIES.find(
                            (c) => c.value === doc.complianceCategory
                          )?.label || doc.complianceCategory}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {doc.gstin || "-"}
                        </td>
                        <td className="px-3 py-2">
                          {doc.periodMonth
                            ? `Month ${doc.periodMonth}`
                            : doc.periodQuarter
                            ? `Q${doc.periodQuarter}`
                            : "-"}
                          <span className="text-gray-500 text-xs block">
                            FY {doc.fiscalYear}
                          </span>
                        </td>
                        <td className="px-3 py-2">{formatDate(doc.dueDate)}</td>
                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(
                              doc.status
                            )}`}
                          >
                            {doc.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleExpandDoc(doc.id)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              title="View Details"
                            >
                              {expandedDoc === doc.id ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </button>
                            {doc.filePath && (
                              <a
                                href={doc.filePath}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-600 hover:text-indigo-800 p-1"
                                title="View File"
                              >
                                <Eye className="h-4 w-4" />
                              </a>
                            )}
                            <select
                              value={doc.status}
                              onChange={(e) => handleUpdateStatus(doc.id, e.target.value)}
                              className="text-xs border rounded px-1 py-0.5"
                              title="Update Status"
                            >
                              {DOCUMENT_STATUSES.map((s) => (
                                <option key={s.value} value={s.value}>
                                  {s.label}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => {
                                setLinkingDocId(doc.id);
                                fetchLinkedDocuments(doc.id);
                                setShowLinkModal(true);
                              }}
                              className="text-purple-600 hover:text-purple-800 p-1"
                              title="Link Documents"
                            >
                              <Link className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteDocument(doc.id)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedDoc === doc.id && (
                        <tr>
                          <td colSpan="8" className="px-3 py-3 bg-gray-50">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <h4 className="font-medium text-gray-900 mb-2">
                                  Document Details
                                </h4>
                                <div className="space-y-1 text-sm">
                                  <p>
                                    <span className="text-gray-500">
                                      File Name:
                                    </span>{" "}
                                    {doc.fileName || "-"}
                                  </p>
                                  <p>
                                    <span className="text-gray-500">
                                      Frequency:
                                    </span>{" "}
                                    {doc.frequency || "-"}
                                  </p>
                                  <p>
                                    <span className="text-gray-500">
                                      Filed Date:
                                    </span>{" "}
                                    {formatDate(doc.filedDate)}
                                  </p>
                                  <p>
                                    <span className="text-gray-500">
                                      Uploaded By:
                                    </span>{" "}
                                    {doc.uploadedByName || "-"}
                                  </p>
                                  <p>
                                    <span className="text-gray-500">
                                      Verified By:
                                    </span>{" "}
                                    {doc.verifiedByName || "-"}
                                  </p>
                                  {doc.remarks && (
                                    <p>
                                      <span className="text-gray-500">
                                        Remarks:
                                      </span>{" "}
                                      {doc.remarks}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900 mb-2">
                                  Audit Trail
                                </h4>
                                {loadingAudit ? (
                                  <div className="flex items-center gap-2 text-gray-500">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                    Loading...
                                  </div>
                                ) : auditTrail.length > 0 ? (
                                  <div className="space-y-2 max-h-40 overflow-y-auto">
                                    {auditTrail.map((entry, idx) => (
                                      <div
                                        key={idx}
                                        className="text-sm border-l-2 border-blue-300 pl-2"
                                      >
                                        <p className="font-medium">
                                          {entry.fromStatus} → {entry.toStatus}
                                        </p>
                                        <p className="text-gray-500 text-xs">
                                          {entry.changedByName} •{" "}
                                          {formatDate(entry.changedAt)}
                                        </p>
                                        {entry.remarks && (
                                          <p className="text-gray-600 text-xs">
                                            {entry.remarks}
                                          </p>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-gray-500 text-sm">
                                    No audit trail available
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Compliance Documents
              </h3>
              <p className="text-gray-600">
                No compliance documents found for this account.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Tracker Section */}
      {activeSection === "tracker" && (
        <div>
          {filters.category === "all" ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <Filter className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <p className="text-blue-700">
                Please select a compliance category to view the tracker
              </p>
            </div>
          ) : tracker ? (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {COMPLIANCE_CATEGORIES.find((c) => c.value === filters.category)
                    ?.label || filters.category}{" "}
                  Tracker - FY {filters.fiscalYear === "all" ? getCurrentFiscalYear() : filters.fiscalYear}
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">View:</span>
                  <button
                    onClick={() => {
                      setTrackerType("monthly");
                      fetchTracker(filters.category, "monthly");
                    }}
                    className={`px-3 py-1 text-sm rounded-lg ${
                      trackerType === "monthly"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => {
                      setTrackerType("quarterly");
                      fetchTracker(filters.category, "quarterly");
                    }}
                    className={`px-3 py-1 text-sm rounded-lg ${
                      trackerType === "quarterly"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Quarterly
                  </button>
                </div>
              </div>
              {tracker.periods && tracker.periods.length > 0 ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2">
                  {tracker.periods.map((period, idx) => (
                    <div
                      key={idx}
                      className={`p-2 rounded-lg text-center border ${
                        period.status === "FILED"
                          ? "bg-green-50 border-green-200"
                          : period.status === "PENDING"
                          ? "bg-yellow-50 border-yellow-200"
                          : period.status === "OVERDUE"
                          ? "bg-red-50 border-red-200"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <p className="text-xs font-medium text-gray-700">
                        {period.period}
                      </p>
                      <p
                        className={`text-xs mt-1 ${
                          period.status === "FILED"
                            ? "text-green-600"
                            : period.status === "PENDING"
                            ? "text-yellow-600"
                            : period.status === "OVERDUE"
                            ? "text-red-600"
                            : "text-gray-500"
                        }`}
                      >
                        {period.status || "-"}
                      </p>
                      {period.documentCount > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {period.documentCount} doc(s)
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">
                  No tracker data available
                </p>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Upload className="h-5 w-5 text-green-600" />
                Upload Compliance Document
              </h3>
              <button
                onClick={handleCloseUploadModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4">
              {/* Category & Document Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Compliance Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={uploadForm.complianceCategory}
                    onChange={(e) =>
                      handleUploadFormChange("complianceCategory", e.target.value)
                    }
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Select Category</option>
                    {COMPLIANCE_CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Document Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={uploadForm.documentType}
                    onChange={(e) =>
                      handleUploadFormChange("documentType", e.target.value)
                    }
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    disabled={!uploadForm.complianceCategory}
                  >
                    <option value="">Select Document Type</option>
                    {getDocumentTypesByCategory(uploadForm.complianceCategory).map(
                      (type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              {/* Other Document Type (if OTHER selected) */}
              {uploadForm.documentType === "OTHER" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Specify Document Type <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={uploadForm.documentTypeOther}
                    onChange={(e) =>
                      handleUploadFormChange("documentTypeOther", e.target.value)
                    }
                    placeholder="Enter document type"
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              )}

              {/* Frequency & Fiscal Year */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Frequency
                  </label>
                  <select
                    value={uploadForm.frequency}
                    onChange={(e) =>
                      handleUploadFormChange("frequency", e.target.value)
                    }
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    {DOCUMENT_FREQUENCIES.map((freq) => (
                      <option key={freq.value} value={freq.value}>
                        {freq.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fiscal Year
                  </label>
                  <select
                    value={uploadForm.fiscalYear}
                    onChange={(e) =>
                      handleUploadFormChange("fiscalYear", e.target.value)
                    }
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    {getFiscalYearOptions().map((fy) => (
                      <option key={fy} value={fy}>
                        FY {fy}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Period (Month/Quarter based on frequency) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {uploadForm.frequency === "MONTHLY" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Period Month
                    </label>
                    <select
                      value={uploadForm.periodMonth || ""}
                      onChange={(e) =>
                        handleUploadFormChange(
                          "periodMonth",
                          e.target.value ? parseInt(e.target.value) : null
                        )
                      }
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="">Select Month</option>
                      {MONTHS.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {uploadForm.frequency === "QUARTERLY" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Period Quarter
                    </label>
                    <select
                      value={uploadForm.periodQuarter || ""}
                      onChange={(e) =>
                        handleUploadFormChange(
                          "periodQuarter",
                          e.target.value ? parseInt(e.target.value) : null
                        )
                      }
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="">Select Quarter</option>
                      {QUARTERS.map((q) => (
                        <option key={q.value} value={q.value}>
                          {q.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    GSTIN (Optional)
                  </label>
                  <input
                    type="text"
                    value={uploadForm.gstin}
                    onChange={(e) =>
                      handleUploadFormChange("gstin", e.target.value.toUpperCase())
                    }
                    placeholder="e.g., 22ABCDE1234F1Z5"
                    maxLength={15}
                    className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                  />
                </div>
              </div>

              {/* Due Date & Year End Critical */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={uploadForm.dueDate}
                    onChange={(e) =>
                      handleUploadFormChange("dueDate", e.target.value)
                    }
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div className="flex items-center pt-6">
                  <input
                    type="checkbox"
                    id="isYearEndCritical"
                    checked={uploadForm.isYearEndCritical}
                    onChange={(e) =>
                      handleUploadFormChange("isYearEndCritical", e.target.checked)
                    }
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="isYearEndCritical"
                    className="ml-2 text-sm text-gray-700"
                  >
                    Year-End Critical Document
                  </label>
                </div>
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Remarks (Optional)
                </label>
                <textarea
                  value={uploadForm.remarks}
                  onChange={(e) =>
                    handleUploadFormChange("remarks", e.target.value)
                  }
                  placeholder="Add any notes or remarks..."
                  rows={2}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload File <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    id="compliance-file-upload"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  />
                  <label
                    htmlFor="compliance-file-upload"
                    className="cursor-pointer"
                  >
                    {selectedFile ? (
                      <div className="flex items-center justify-center gap-2 text-green-600">
                        <FileText className="h-6 w-6" />
                        <span className="text-sm font-medium">
                          {selectedFile.name}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedFile(null);
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="text-gray-500">
                        <Upload className="h-8 w-8 mx-auto mb-2" />
                        <p className="text-sm">
                          Click to upload or drag and drop
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          PDF, DOC, DOCX, XLS, XLSX, JPG, PNG
                        </p>
                      </div>
                    )}
                  </label>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
              <button
                onClick={handleCloseUploadModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                onClick={handleUploadDocument}
                disabled={uploading}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Upload Document
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Link Documents Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Link className="h-5 w-5 text-purple-600" />
                Link Documents
              </h3>
              <button
                onClick={() => {
                  setShowLinkModal(false);
                  setLinkingDocId(null);
                  setLinkedDocs([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Currently Linked Documents */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Currently Linked Documents</h4>
                {linkedDocs.length > 0 ? (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {linkedDocs.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-2 bg-purple-50 rounded border border-purple-100">
                        <div>
                          <p className="text-sm font-medium">{doc.documentType}</p>
                          <p className="text-xs text-gray-500">{doc.linkageType || "Linked"}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(doc.status)}`}>
                          {doc.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No linked documents</p>
                )}
              </div>

              {/* Link New Document */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Link to Another Document</h4>
                <div className="space-y-3">
                  <select
                    id="linkTargetDoc"
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    defaultValue=""
                  >
                    <option value="">Select a document to link...</option>
                    {documents
                      .filter((d) => d.id !== linkingDocId)
                      .map((doc) => (
                        <option key={doc.id} value={doc.id}>
                          {doc.documentType} - {doc.fiscalYear} ({doc.status})
                        </option>
                      ))}
                  </select>
                  <select
                    id="linkageType"
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    defaultValue="RELATED"
                  >
                    <option value="RELATED">Related Document</option>
                    <option value="ACKNOWLEDGEMENT">Acknowledgement</option>
                    <option value="AMENDMENT">Amendment</option>
                    <option value="SUPPORTING">Supporting Document</option>
                    <option value="REVISION">Revision</option>
                  </select>
                  <button
                    onClick={() => {
                      const targetDoc = document.getElementById("linkTargetDoc").value;
                      const linkageType = document.getElementById("linkageType").value;
                      if (targetDoc) {
                        handleLinkDocument(linkingDocId, targetDoc, linkageType);
                      } else {
                        toast.error("Please select a document to link");
                      }
                    }}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2"
                  >
                    <Link className="h-4 w-4" />
                    Link Documents
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end p-4 border-t bg-gray-50">
              <button
                onClick={() => {
                  setShowLinkModal(false);
                  setLinkingDocId(null);
                  setLinkedDocs([]);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceTab;
